import React, { useState } from "react";
import { Send, Mail, MapPin, CheckCircle } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
    location_preference: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const locationOptions = [
    { value: "restaurant", label: "Restaurant / Café" },
    { value: "bar", label: "Bar / Nattklubb" },
    { value: "event", label: "Event / Festival" },
    { value: "hotel", label: "Hotell / Overnatting" },
    { value: "other", label: "Annet" },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData((prev) => ({ ...prev, location_preference: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success("Meldingen din er sendt! Vi tar kontakt snart.");
      setFormData({
        name: "",
        email: "",
        message: "",
        location_preference: "",
      });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Noe gikk galt. Prøv igjen senere.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="py-24 md:py-32 bg-[#121212]"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Contact Info */}
          <div>
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FFD84D] mb-4 block">
              Kontakt
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white mb-6 font-['Outfit']">
              Ta kontakt
            </h2>
            <p className="text-lg text-[#A1A1AA] mb-10 leading-relaxed">
              Interessert i å få en B-Labs stasjon hos deg? Fyll ut skjemaet så
              tar vi kontakt innen 24 timer.
            </p>

            {/* Contact Details */}
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#0A0A0A] border border-white/10 rounded-xl flex items-center justify-center">
                  <Mail className="w-5 h-5 text-[#FFD84D]" />
                </div>
                <div>
                  <p className="text-sm text-[#A1A1AA]">E-post</p>
                  <a
                    href="mailto:brienlabs.hq@gmail.com"
                    className="text-white hover:text-[#FFD84D] transition-colors"
                    data-testid="contact-email"
                  >
                    brienlabs.hq@gmail.com
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#0A0A0A] border border-white/10 rounded-xl flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-[#FFD84D]" />
                </div>
                <div>
                  <p className="text-sm text-[#A1A1AA]">Lokasjon</p>
                  <p className="text-white">Norge</p>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="bg-[#0A0A0A] border border-white/10 rounded-2xl p-8 md:p-10">
            {isSubmitted ? (
              <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
                <div className="w-16 h-16 bg-[#FFD84D]/10 rounded-full flex items-center justify-center mb-6">
                  <CheckCircle className="w-8 h-8 text-[#FFD84D]" />
                </div>
                <h3 className="text-2xl font-semibold text-white mb-2 font-['Outfit']">
                  Takk for meldingen!
                </h3>
                <p className="text-[#A1A1AA] mb-6">
                  Vi tar kontakt med deg så snart som mulig.
                </p>
                <button
                  onClick={() => setIsSubmitted(false)}
                  className="text-[#FFD84D] hover:underline"
                  data-testid="send-another-message"
                >
                  Send ny melding
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name */}
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-white mb-2"
                  >
                    Navn *
                  </label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Ditt navn"
                    className="bg-[#121212] border-white/10 text-white placeholder:text-[#A1A1AA]/50 focus:border-[#FFD84D] focus:ring-[#FFD84D]/20"
                    data-testid="contact-name-input"
                  />
                </div>

                {/* Email */}
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-white mb-2"
                  >
                    E-post *
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="din@epost.no"
                    className="bg-[#121212] border-white/10 text-white placeholder:text-[#A1A1AA]/50 focus:border-[#FFD84D] focus:ring-[#FFD84D]/20"
                    data-testid="contact-email-input"
                  />
                </div>

                {/* Location Preference */}
                <div>
                  <label
                    htmlFor="location"
                    className="block text-sm font-medium text-white mb-2"
                  >
                    Hvor ønsker du å plassere maskinen?
                  </label>
                  <Select
                    value={formData.location_preference}
                    onValueChange={handleSelectChange}
                  >
                    <SelectTrigger
                      className="bg-[#121212] border-white/10 text-white focus:ring-[#FFD84D]/20"
                      data-testid="contact-location-select"
                    >
                      <SelectValue placeholder="Velg type lokasjon" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-white/10">
                      {locationOptions.map((option) => (
                        <SelectItem
                          key={option.value}
                          value={option.value}
                          className="text-white hover:bg-[#FFD84D]/10 focus:bg-[#FFD84D]/10"
                          data-testid={`location-option-${option.value}`}
                        >
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Message */}
                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm font-medium text-white mb-2"
                  >
                    Melding *
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Fortell oss om din bedrift og dine behov..."
                    rows={5}
                    className="bg-[#121212] border-white/10 text-white placeholder:text-[#A1A1AA]/50 focus:border-[#FFD84D] focus:ring-[#FFD84D]/20 resize-none"
                    data-testid="contact-message-input"
                  />
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="contact-submit-button"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                      Sender...
                    </>
                  ) : (
                    <>
                      Send melding
                      <Send className="w-5 h-5" />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
